"use client";
/* eslint-disable @next/next/no-img-element */
import { useState, useRef, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  sizes?: string;
}

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = '',
  loading = 'lazy',
  priority = false,
  sizes = '100vw'
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // If priority, load immediately
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        // Increase root margin to start loading earlier
        rootMargin: '200px',
        threshold: 0.01
      }
    );

    if (imgRef.current && loading === 'lazy') {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [loading, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Generate responsive image sources
  const generateSrcSet = (baseSrc: string) => {
    const ext = baseSrc.split('.').pop();

    // For WebP images, provide different sizes
    if (ext === 'webp') {
      return `${baseSrc} 1x, ${baseSrc} 2x`;
    }

    return baseSrc;
  };

  return (
    <div
      ref={imgRef}
      className={`relative ${className}`}
      style={{
        aspectRatio: width && height ? `${width}/${height}` : undefined,
        backgroundColor: isLoaded ? 'transparent' : '#f3f4f6'
      }}
    >
      {/* Only show image when it should be in view */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          srcSet={generateSrcSet(src)}
          sizes={sizes}
          className={`${className} transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          decoding="async"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      )}
    </div>
  );
}